import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Copy, ArrowClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

export default function DuplicateVoucherLogPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/voucher-stock/duplicates');
      setItems(r.data || []);
    } catch (err) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const crossCount = items.filter((x) => x.cross_vendor).length;

  return (
    <div className="space-y-5" data-testid="duplicate-log-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Copy size={26} weight="duotone" className="text-amber-600" /> Duplicate Voucher Log
        </h1>
        <span className="text-sm text-gray-500">Jab koi voucher code pehle se exist karta hai aur dobara upload hone ki koshish hoti hai, yahan log ho jata hai.</span>
        <Button variant="outline" size="sm" onClick={load} className="ml-auto"><ArrowClockwise size={14} /> Refresh</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-amber-700 font-semibold">Total Duplicates</div>
          <div className="text-xl font-bold text-amber-900">{items.length}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-red-700 font-semibold">Cross-Vendor</div>
          <div className="text-xl font-bold text-red-900">{crossCount}</div>
          <div className="text-[10px] text-red-600 mt-0.5">Warning: same code on different vendors</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="text-[11px] uppercase text-gray-500 font-semibold">Same Vendor Re-uploads</div>
          <div className="text-xl font-bold text-gray-900">{items.length - crossCount}</div>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Koi duplicate attempts nahi — clean! ✓</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Attempt Date</th>
                  <th className="px-3 py-2 text-left">Code</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Attempted by</th>
                  <th className="px-3 py-2 text-left">Original Vendor</th>
                  <th className="px-3 py-2 text-left">Original Upload</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className={`border-t ${d.cross_vendor ? 'bg-red-50/50' : ''}`} data-testid={`dup-row-${d.id}`}>
                    <td className="px-3 py-2 text-xs">{fmtDate(d.created_at)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{d.code}</td>
                    <td className="px-3 py-2 text-right">₹{(d.attempt_amount || 0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-xs">{d.attempted_vendor_name} <span className="text-gray-400">• {d.uploaded_by_name}</span></td>
                    <td className="px-3 py-2 text-xs font-semibold">{d.original_vendor_name || '—'}</td>
                    <td className="px-3 py-2 text-xs">{fmtDate(d.original_upload_date)}</td>
                    <td className="px-3 py-2">
                      {d.cross_vendor ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700">CROSS-VENDOR</span>
                      ) : (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700">SAME VENDOR</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">{d.action_taken}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
